import React, { useCallback, useMemo } from "react";
import {
  Button,
  Grid,
  IconButton,
  Icons,
  Text,
  Card,
  ProgressBar,
  Loader,
  Divider,
  Avatar,
} from "@pushpress/shared-components";
import { removeTimezoneFromDateString } from "../../../utils/dateUtils";
import { format } from "date-fns";
import Dashboard from "../../../layouts/Dashboard";
import { useHistory, useRouteMatch } from "react-router-dom";
import { PRIMARY_COLOR } from "../../../constants/colors";
import {
  formatRegistrationEnd,
  formatRegistrationStart,
} from "../Calendar.utils";
import { defaultToastOptions, getInitials, sleep } from "../../../utils/misc";
import { useToggle } from "../../../hooks/state";
import { toast } from "react-toastify";
import CancelCalendarItem from "../CalendarForm/CancelCalendarItem";
import {
  GetCalendarItemDetailQuery,
  useCoachCheckinMutation,
} from "./CalendarDetail-generated.hooks";
import { isInviteOnly, source } from "./CalendarDetail.utils";
import styles from "./ClassSummary.module.scss";
import IconsMenu from "../components/IconMenu";
import CalendarActionsRouter from "../CalendarActionsRouter";
import { isCypress } from "../../../utils/cypress";
import { Badge } from "@pushpress/pushpress-ui";

interface ClassSummaryProps {
  calendarItem: GetCalendarItemDetailQuery["getCalendarItem"];
  refetch: VoidFunction;
}

function isEvent(calendarItem: GetCalendarItemDetailQuery["getCalendarItem"]) {
  return calendarItem.type === "Event";
}

function isClass(calendarItem: GetCalendarItemDetailQuery["getCalendarItem"]) {
  return calendarItem.type === "Class";
}

export const ClassSummary = (props: ClassSummaryProps) => {
  const { calendarItem, refetch } = props;
  const routeMatch = useRouteMatch(`/schedule/${calendarItem?.uuid}`);
  const path = isCypress ? `/schedule/${calendarItem?.uuid}` : routeMatch?.path;
  const history = useHistory();
  const [coachCheckInMutation] = useCoachCheckinMutation();

  const [showDeleteModal, showDeleteModalActions] = useToggle(false);

  const { mainCoachCheckIn, assistantCoachCheckIn } = useMemo(
    () => ({
      mainCoachCheckIn: calendarItem?.attendees?.some(
        (attendee) => attendee.userUuid === calendarItem?.mainCoach?.userUuid,
      ),
      assistantCoachCheckIn: calendarItem?.attendees?.some(
        (attendee) =>
          attendee.userUuid === calendarItem?.assistantCoach?.userUuid,
      ),
    }),
    [
      calendarItem?.attendees,
      calendarItem?.mainCoach?.userUuid,
      calendarItem?.assistantCoach?.userUuid,
    ],
  );

  const handleCheckIn = async (userUuid: string) => {
    if (calendarItem) {
      await coachCheckInMutation({
        variables: {
          coachCheckinInput: {
            calendarItemUuid: calendarItem?.uuid,
            coachUserUuid: userUuid,
            source,
          },
        },
      });
      toast.success(`Coach check in successfully done!`, defaultToastOptions);
      // unfortunately we have the issue that backend don't reflect the changes instantaneous
      await sleep(1000);
      refetch();
    }
  };

  const handleOpenEditDrawers = useCallback(() => {
    if (!calendarItem) return;

    if (isClass(calendarItem)) {
      history.push(`${path}/edit/class/${calendarItem.uuid}`);
    }

    if (isEvent(calendarItem)) {
      history.push(`${path}/edit/event/${calendarItem.uuid}`);
    }
  }, [calendarItem, history, path]);

  if (!calendarItem)
    return (
      <Dashboard>
        <Grid container justifyContent="center" alignItems="center" marginY={5}>
          <Grid item>
            <Loader />
          </Grid>
        </Grid>
      </Dashboard>
    );

  return (
    <>
      <CalendarActionsRouter refetch={refetch} baseUrl={path} />
      <CancelCalendarItem
        visible={showDeleteModal}
        uuid={calendarItem.uuid}
        onClose={showDeleteModalActions.off}
        onCalendarItemCancelled={() => history.push("/schedule")}
      />
      <Card>
        <div className={styles.content}>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            {isInviteOnly(calendarItem) && (
              <Grid item className={styles.pushLeft}>
                <Badge>Invite-only</Badge>
              </Grid>
            )}
            <Grid item>
              <IconButton
                icon={Icons.Pencil}
                onClick={handleOpenEditDrawers}
                className="tw-mr-auto"
                size={22}
              />
            </Grid>
            <Grid item>
              {isClass(calendarItem) ? (
                <IconsMenu
                  icon={Icons.Kebabs}
                  menuItems={[
                    {
                      label: "Delete",
                      onClick: showDeleteModalActions.on,
                    },
                    {
                      label: "Duplicate",
                      onClick: () =>
                        history.push(
                          `${path}/duplicate/class/${calendarItem.uuid}`,
                        ),
                    },
                  ]}
                />
              ) : (
                <IconButton
                  icon={Icons.Trash}
                  size={22}
                  onClick={showDeleteModalActions.on}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} wrap="nowrap" className="mt-1">
            <Grid item>
              <div
                className={`${styles.square} mt-4`}
                style={{
                  backgroundColor: calendarItem.calendarItemType?.color || "",
                }}
              />
            </Grid>
            <Grid item>
              <Text variant="heading-4" color="wolf-800" className="mb-2">
                {calendarItem.title}
              </Text>
              <Text
                variant="body-lg"
                color="wolf-600"
                className={styles.dateDescription}
              >
                {format(
                  removeTimezoneFromDateString(calendarItem.startDatetime),
                  "EEEE, MMMM d",
                )}
                <span className={styles.bulletSeparator}>&#8226;</span>
                <span className={styles.startEndTime}>
                  {format(
                    removeTimezoneFromDateString(calendarItem.startDatetime),
                    "h:mma",
                  )}{" "}
                  -{" "}
                  {format(
                    removeTimezoneFromDateString(calendarItem.endDatetime),
                    "h:mma",
                  )}
                </span>
              </Text>
              <Text variant="body-lg" color="wolf-600">
                {/* Weekly on weekdays */}
              </Text>
            </Grid>
          </Grid>
          {calendarItem.mainCoach && (
            <Grid
              container
              alignItems="center"
              className="mt-4"
              justifyContent="space-between"
            >
              <Grid item>
                <Grid container alignItems="center" spacing={2} wrap="nowrap">
                  <Grid item>
                    <Avatar size="small">
                      {calendarItem.mainCoach?.primaryImage ? (
                        <img
                          src={calendarItem.mainCoach?.primaryImage}
                          alt="Coach"
                        />
                      ) : (
                        getInitials(
                          calendarItem.mainCoach?.firstName,
                          calendarItem.mainCoach?.lastName,
                        )
                      )}
                    </Avatar>
                  </Grid>
                  <Grid item>
                    <Text variant="body-md" color="wolf-600">
                      Coach
                    </Text>
                    <Text variant="body-lg" color="wolf-800">
                      {calendarItem.mainCoach
                        ? `${calendarItem.mainCoach.firstName} ${calendarItem.mainCoach.lastName}`
                        : "None"}
                    </Text>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  onClick={() => {
                    if (!mainCoachCheckIn)
                      handleCheckIn(calendarItem.mainCoach?.userUuid);
                  }}
                  data-cy="coach-check-in"
                  buttonType={mainCoachCheckIn ? "primary" : "secondary"}
                  text={mainCoachCheckIn ? "Checked in" : "Check in"}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
          {calendarItem.assistantCoach && (
            <Grid
              container
              alignItems="center"
              className="mt-4"
              justifyContent="space-between"
              wrap="nowrap"
            >
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Avatar size="small">
                      {calendarItem.assistantCoach?.primaryImage ? (
                        <img
                          src={calendarItem.assistantCoach?.primaryImage}
                          alt="Assistant Coach"
                        />
                      ) : (
                        getInitials(
                          calendarItem.assistantCoach?.firstName,
                          calendarItem.assistantCoach?.lastName,
                        )
                      )}
                    </Avatar>
                  </Grid>
                  <Grid item>
                    <Text variant="body-md" color="wolf-600">
                      Assistant coach
                    </Text>
                    <Text variant="body-lg" color="wolf-800">
                      {calendarItem.assistantCoach
                        ? `${calendarItem.assistantCoach.firstName} ${calendarItem.assistantCoach.lastName}`
                        : "None"}
                    </Text>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  onClick={() => {
                    if (!assistantCoachCheckIn)
                      handleCheckIn(calendarItem.assistantCoach?.userUuid);
                  }}
                  data-cy="assistant-coach-check-in"
                  buttonType={assistantCoachCheckIn ? "primary" : "secondary"}
                  text={assistantCoachCheckIn ? "Checked in" : "Check in"}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
          {isEvent(calendarItem) && (
            <Button
              component="a"
              buttonType="secondary"
              text="Landing page"
              icon={Icons.NewPage}
              iconLocation="right"
              fullWidth
              size="large"
              className="mt-4"
              target="_blank"
              href={`/landing/events/${calendarItem.uuid}`}
            />
          )}
        </div>
        <Divider />
        <div className={styles.content}>
          {Number(calendarItem.attendanceCap) > 0 && (
            <div className="mb-4">
              <Text variant="body-md" color="wolf-600">
                Attendees
              </Text>
              <Text variant="body-lg" color="wolf-800">
                {calendarItem.attendanceCap -
                  Number(calendarItem.spotsAvailable)}{" "}
                / {calendarItem.attendanceCap}
              </Text>
              <div className={`${styles.progressBar} mt-4`}>
                <ProgressBar
                  label=""
                  color={PRIMARY_COLOR}
                  progressWidth={
                    ((calendarItem.attendanceCap -
                      Number(calendarItem.spotsAvailable)) *
                      100) /
                    Number(calendarItem.attendanceCap)
                  }
                />
              </div>
            </div>
          )}
          {calendarItem.location && (
            <div className="mb-4">
              <Text variant="body-md" color="wolf-600">
                Location
              </Text>
              <Text variant="body-lg" color="wolf-800">
                {calendarItem.location?.name}
              </Text>
            </div>
          )}

          {isInviteOnly(calendarItem) && (
            <div className="mb-4">
              <Text variant="body-md" color="wolf-600">
                Invite only event
              </Text>
              <Text variant="body-lg" color="wolf-800">
                Only people who are invited to this event will be able to
                reserve a spot and attend.
              </Text>
            </div>
          )}
          <div>
            <Text variant="body-md" color="wolf-600">
              Reservations open
            </Text>
            <Text variant="body-lg" color="wolf-800">
              {formatRegistrationStart(calendarItem.registrationStartOffset)}
            </Text>
          </div>
          <div className="mt-4">
            <Text variant="body-md" color="wolf-600">
              Reservations close
            </Text>
            <Text variant="body-lg" color="wolf-800">
              {formatRegistrationEnd(calendarItem.registrationEndOffset)}
            </Text>
          </div>
          {calendarItem?.type !== "Event" && (
            <div className="mt-4" data-cy="late-cancel">
              <Text variant="body-md" color="wolf-600">
                Late cancel
              </Text>
              <Text variant="body-lg" color="wolf-800">
                {formatRegistrationEnd(
                  calendarItem.registrationLateCancelOffset,
                )}
              </Text>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};

export default ClassSummary;
